import React, { useState } from 'react';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Sample data for demonstrations
const sampleUsers = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@email.com',
    role: 'Admin',
    status: 'Active',
    events: 12,
    spent: 1250.0,
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    role: 'User',
    status: 'Active',
    events: 8,
    spent: 890.5,
  },
  {
    id: 3,
    name: 'Mike Williams',
    email: 'mike.w@email.com',
    role: 'User',
    status: 'Inactive',
    events: 3,
    spent: 245.0,
  },
  {
    id: 4,
    name: 'Emily Davis',
    email: 'emily.d@email.com',
    role: 'Staff',
    status: 'Active',
    events: 15,
    spent: 1890.25,
  },
  {
    id: 5,
    name: 'Chris Brown',
    email: 'chris.b@email.com',
    role: 'User',
    status: 'Pending',
    events: 0,
    spent: 0,
  },
];

const DataTableDocs = () => {
  const [currentPage, setCurrentPage] = useState(1);

  // Basic columns
  const basicColumns = [
    { field: 'name', header: 'Name' },
    { field: 'email', header: 'Email' },
    { field: 'role', header: 'Role' },
  ];

  // Columns with custom rendering
  const advancedColumns = [
    {
      field: 'name',
      header: 'User',
      subField: 'email',
    },
    {
      field: 'role',
      header: 'Role',
      render: (item) => <Badge variant="info">{item.role}</Badge>,
    },
    {
      field: 'status',
      header: 'Status',
      render: (item) => (
        <Badge
          variant={
            item.status === 'Active'
              ? 'success'
              : item.status === 'Inactive'
              ? 'error'
              : 'warning'
          }
        >
          {item.status}
        </Badge>
      ),
    },
    {
      field: 'spent',
      header: 'Total Spent',
      render: (item) => `$${item.spent.toFixed(2)}`,
    },
  ];

  // Basic actions
  const basicActions = [
    {
      type: 'view',
      title: 'View Details',
      onClick: (item) => alert(`View: ${item.name}`),
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: (item) => alert(`Edit: ${item.name}`),
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: (item) => alert(`Delete: ${item.name}`),
    },
  ];

  const dataTableProps = [
    {
      name: 'columns',
      type: 'array',
      required: true,
      description:
        'Column definitions. Each column has: field (string), header (string), subField (string), render (function), headerStyle, cellStyle.',
    },
    {
      name: 'data',
      type: 'array',
      required: true,
      description: 'Array of data objects to display in the table.',
    },
    {
      name: 'loading',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Show skeleton loading state while data is being fetched.',
    },
    {
      name: 'emptyMessage',
      type: 'string',
      default: "'No data found'",
      required: false,
      description: 'Message to display when data array is empty.',
    },
    {
      name: 'emptySubtext',
      type: 'string',
      default: "'Try adjusting your search or filters'",
      required: false,
      description: 'Secondary text for empty state.',
    },
    {
      name: 'emptyIcon',
      type: 'node',
      required: false,
      description: 'Custom icon for empty state.',
    },
    {
      name: 'actions',
      type: 'array',
      default: '[]',
      required: false,
      description:
        "Action button configurations. Each action has: type ('view'|'edit'|'delete'|'message'|'credit'), title, icon, onClick, to, wrapper, disabled.",
    },
    {
      name: 'keyField',
      type: 'string',
      default: "'id'",
      required: false,
      description: 'Field to use as unique key for each row.',
    },
    {
      name: 'currentPage',
      type: 'number',
      default: '1',
      required: false,
      description: 'Current page number for pagination.',
    },
    {
      name: 'totalPages',
      type: 'number',
      default: '1',
      required: false,
      description: 'Total number of pages.',
    },
    {
      name: 'onPageChange',
      type: 'function',
      required: false,
      description: 'Callback when page changes. Receives new page number.',
    },
    {
      name: 'mobileCardRenderer',
      type: 'function',
      required: false,
      description:
        'Custom renderer for mobile cards. Receives (item, renderActions, renderMobileActions).',
    },
    {
      name: 'skeletonRows',
      type: 'number',
      default: '5',
      required: false,
      description: 'Number of skeleton rows to show during loading.',
    },
    {
      name: 'title',
      type: 'string',
      required: false,
      description: 'Optional section title displayed above the table.',
    },
    {
      name: 'actionsRenderer',
      type: 'function',
      required: false,
      description: 'Custom actions renderer. Receives item, returns JSX.',
    },
    {
      name: 'mobileQuickInfo',
      type: 'array',
      required: false,
      description:
        'Fields to show in mobile quick info section. Each has: field, label, render.',
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>DataTable</Title>
        <Subtitle>
          A flexible, responsive table component with built-in pagination,
          loading states, mobile card views, and action buttons. Supports custom
          column rendering, theming, and is fully accessible.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          The simplest usage of DataTable with columns and data. The component
          automatically handles rendering and responsive layout.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={basicColumns}
            data={sampleUsers.slice(0, 3)}
            keyField="id"
          />
        </Card>

        <CodeBlock>
          {`import DataTable from 'components/DataTable';

const columns = [
  { field: 'name', header: 'Name' },
  { field: 'email', header: 'Email' },
  { field: 'role', header: 'Role' },
];

const data = [
  { id: 1, name: 'John Smith', email: 'john@email.com', role: 'Admin' },
  { id: 2, name: 'Sarah Johnson', email: 'sarah@email.com', role: 'User' },
];

<DataTable
  columns={columns}
  data={data}
  keyField="id"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Actions</SectionTitle>
        <SectionDescription>
          Add action buttons to each row using the actions prop. Built-in action
          types include: view, edit, delete, message, and credit. Each shows an
          appropriate icon automatically.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={basicColumns}
            data={sampleUsers.slice(0, 3)}
            keyField="id"
            actions={basicActions}
          />
        </Card>

        <CodeBlock>
          {`const actions = [
  {
    type: 'view',
    title: 'View Details',
    onClick: (item) => handleView(item),
  },
  {
    type: 'edit',
    title: 'Edit',
    onClick: (item) => handleEdit(item),
  },
  {
    type: 'delete',
    title: 'Delete',
    onClick: (item) => handleDelete(item),
  },
];

<DataTable
  columns={columns}
  data={data}
  actions={actions}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Custom Column Rendering</SectionTitle>
        <SectionDescription>
          Use the render function in column definitions to customize how cell
          content is displayed. This is useful for formatting dates, currency,
          or displaying badges and other components.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={advancedColumns}
            data={sampleUsers}
            keyField="id"
            actions={[
              {
                type: 'view',
                title: 'View',
                onClick: (item) => alert(`View: ${item.name}`),
              },
            ]}
          />
        </Card>

        <CodeBlock>
          {`import Badge from 'components/Badge';

const columns = [
  {
    field: 'name',
    header: 'User',
    subField: 'email', // Shows email as subtitle
  },
  {
    field: 'role',
    header: 'Role',
    render: (item) => <Badge variant="info">{item.role}</Badge>,
  },
  {
    field: 'status',
    header: 'Status',
    render: (item) => (
      <Badge variant={item.status === 'Active' ? 'success' : 'error'}>
        {item.status}
      </Badge>
    ),
  },
  {
    field: 'spent',
    header: 'Total Spent',
    render: (item) => \`$\${item.spent.toFixed(2)}\`,
  },
];`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Pagination</SectionTitle>
        <SectionDescription>
          Enable pagination by providing currentPage, totalPages, and
          onPageChange props. The pagination component automatically handles
          ellipsis for large page counts.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={basicColumns}
            data={sampleUsers.slice(0, 2)}
            keyField="id"
            currentPage={currentPage}
            totalPages={5}
            onPageChange={setCurrentPage}
          />
        </Card>

        <CodeBlock>
          {`const [currentPage, setCurrentPage] = useState(1);
const totalPages = Math.ceil(totalCount / itemsPerPage);

<DataTable
  columns={columns}
  data={data}
  currentPage={currentPage}
  totalPages={totalPages}
  onPageChange={setCurrentPage}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Loading State</SectionTitle>
        <SectionDescription>
          When loading is true, the table displays a skeleton loading state.
          Customize the number of skeleton rows with the skeletonRows prop.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={basicColumns}
            data={[]}
            loading={true}
            skeletonRows={3}
          />
        </Card>

        <CodeBlock>
          {`<DataTable
  columns={columns}
  data={data}
  loading={isLoading}
  skeletonRows={5}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Empty State</SectionTitle>
        <SectionDescription>
          When data is empty, a customizable empty state is displayed. You can
          customize the message, subtext, and icon.
        </SectionDescription>

        <Card style={{ marginBottom: 24 }}>
          <DataTable
            columns={basicColumns}
            data={[]}
            emptyMessage="No users found"
            emptySubtext="Try adjusting your search filters or add a new user"
          />
        </Card>

        <CodeBlock>
          {`<DataTable
  columns={columns}
  data={[]}
  emptyMessage="No users found"
  emptySubtext="Try adjusting your search filters"
  emptyIcon={<CustomIcon />}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Action Types</SectionTitle>
        <SectionDescription>
          DataTable comes with built-in icons for common action types. You can
          also provide custom icons or use link navigation.
        </SectionDescription>

        <ComponentDemo direction="column" gap="16px">
          <Card>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <strong>Built-in Action Types:</strong>
              <br />• <code>view</code> - Eye icon for viewing details
              <br />• <code>edit</code> - Pencil icon for editing
              <br />• <code>delete</code> - Trash icon for deleting
              <br />• <code>message</code> - Envelope icon for messaging
              <br />• <code>credit</code> - Credit card icon for payments
            </div>
          </Card>
        </ComponentDemo>

        <CodeBlock>
          {`// Action with onClick handler
{
  type: 'edit',
  title: 'Edit User',
  onClick: (item) => openEditModal(item),
}

// Action with link navigation
{
  type: 'view',
  title: 'View Details',
  to: (item) => \`/admin/users/\${item.id}\`,
}

// Action with custom icon
{
  title: 'Custom Action',
  icon: <CustomIcon />,
  onClick: (item) => handleCustomAction(item),
}

// Disabled action
{
  type: 'delete',
  title: 'Delete',
  onClick: handleDelete,
  disabled: !canDelete,
}`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Component Props</SectionTitle>
        <PropsTable props={dataTableProps} />
      </Section>

      <Section>
        <SectionTitle>Mobile Responsiveness</SectionTitle>
        <SectionDescription>
          On mobile devices (screen width less than 768px), the table
          automatically switches to a card-based layout. Each row becomes a card
          with the first column as the title and remaining columns as detail
          rows. Actions are displayed at the bottom of each card.
        </SectionDescription>

        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Mobile Features:</strong>
            <br />• Automatic card-based layout on small screens
            <br />• First column becomes card title with optional subtitle
            (subField)
            <br />• Remaining columns shown as label/value pairs
            <br />• Actions rendered at bottom of each card
            <br />• Custom mobile card renderer available via mobileCardRenderer
            prop
            <br />• mobileQuickInfo for compact grid display of key fields
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Usage Guidelines</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use DataTable for any list/table data display
              <br />• Provide meaningful column headers
              <br />• Include action tooltips (title prop) for accessibility
              <br />• Use custom render functions for formatted data (dates,
              currency, badges)
              <br />• Implement pagination for large datasets
              <br />• Show loading state while fetching data
              <br />• Provide helpful empty state messages
            </div>
          </Card>

          <Card title="Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't display too many columns (5-7 max recommended)
              <br />• Don't skip the loading state during data fetches
              <br />• Don't use generic empty messages like "No data"
              <br />• Don't forget to handle action errors gracefully
              <br />• Don't mix too many action types in one table
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Real-World Example</SectionTitle>
        <SectionDescription>
          Here's how DataTable is used in the Waivers page:
        </SectionDescription>

        <CodeBlock>
          {`import DataTable from 'components/DataTable';

const WaiversPage = () => {
  const columns = [
    { field: 'name', header: 'Waiver Name' },
    { field: 'waiver_id', header: 'Waiver ID' },
  ];

  const actions = [
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditWaiver,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={waivers}
      loading={loading}
      keyField="id"
      emptyMessage="No waivers found"
      emptySubtext="Try adjusting your search or create a new waiver"
      actions={actions}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
    />
  );
};`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default DataTableDocs;
